if (!customElements.get('size-chart')) {
  customElements.define(
    'size-chart',
    class SizeChart extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.sectionId = this.dataset.sectionId;
        this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
        this.baseUnit = this.dataset.unit === 'cm' ? 'cm' : 'inches';

        // Remember each numeric cell's original value so unit switching never compounds rounding
        this.cells = [...this.querySelectorAll('tbody td')].filter((td) => /^\d+(\.\d+)?$/.test(td.textContent.trim()));
        this.cells.forEach((td) => (td.dataset.base = td.textContent.trim()));

        this.querySelector('.size-chart__trigger').addEventListener('click', () => this.open());
        this.querySelector('[data-close]').addEventListener('click', () => this.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close(); // backdrop
        });
        this.dialog.addEventListener('close', () => document.documentElement.classList.remove('size-chart-open'));
        this.querySelectorAll('.size-chart__units button').forEach((button) =>
          button.addEventListener('click', () => this.setUnit(button.dataset.unit))
        );

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.sectionId || !event.data.variant || this.sizeIndex < 0) return;
            this.highlight(event.data.variant.options[this.sizeIndex]);
          });
        }
        this.highlight(this.dataset.selectedSize);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      open() {
        document.documentElement.classList.add('size-chart-open');
        this.dialog.showModal();
        const current = this.querySelector('tr.is-selected');
        if (current) current.scrollIntoView({ block: 'center', inline: 'nearest' });
      }

      close() {
        this.dialog.close();
      }

      setUnit(unit) {
        this.querySelectorAll('.size-chart__units button').forEach((button) =>
          button.setAttribute('aria-pressed', String(button.dataset.unit === unit))
        );
        const factor = unit === this.baseUnit ? 1 : unit === 'cm' ? 2.54 : 1 / 2.54;
        this.cells.forEach((td) => {
          const value = parseFloat(td.dataset.base) * factor;
          td.textContent = factor === 1 ? td.dataset.base : String(Math.round(value * 10) / 10);
        });
        this.querySelector('[data-unit-label]').textContent = unit;
      }

      // Age in years for a size or row label: "Newborn" → 0, "3 YRS" → 3, "2-3 years" → [2, 3], "6-12 months" → [0.5, 1]
      static parseAge(text) {
        const label = (text || '').toLowerCase();
        if (/newborn|^0\b/.test(label)) return { start: 0, end: 0 };
        const range = label.match(/(\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(\d+(?:\.\d+)?)/);
        const single = label.match(/(\d+(?:\.\d+)?)/);
        if (!range && !single) return null;
        const scale = /month|mo\b/.test(label) ? 1 / 12 : 1;
        const start = parseFloat(range ? range[1] : single[1]) * scale;
        const end = parseFloat(range ? range[2] : single[1]) * scale;
        return { start, end };
      }

      // Exact label match first ("3 YRS" row for the "3 YRS" size); otherwise the age-range row the size starts
      // ("3 YRS" → "3-4 years", "1 YRS" → "1-2 years"; lehenga "3 YRS" → "2-4 years")
      highlight(size) {
        const normalise = (text) => (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const wanted = normalise(size);
        const rows = [...this.querySelectorAll('tbody tr')];
        let match = wanted ? rows.find((row) => normalise(row.dataset.size) === wanted) : null;

        if (!match && wanted) {
          const age = SizeChart.parseAge(wanted);
          if (age) {
            const point = age.end;
            match = rows.find((row) => {
              const range = SizeChart.parseAge(row.dataset.size);
              if (!range) return false;
              return point === 0 ? range.end === 0 : range.start <= point && point < range.end;
            });
          }
        }

        rows.forEach((row) => {
          row.classList.toggle('is-selected', row === match);
          if (row === match) row.setAttribute('aria-current', 'true');
          else row.removeAttribute('aria-current');
        });
      }
    }
  );
}
